import { type ParameterCategory } from "components/CC/UI/ParameterPicker/ParameterPicker";
import { type CustomFieldData } from "components/CC/UI/ParameterPicker/parameterUtils.tsx";
import { createCommand, type LexicalCommand } from "lexical";

// ============================================================================
// Types
// ============================================================================

export type TagParamType =
    | "form_fields"
    | "keys"
    | "rows"
    | "system"
    | "steps"
    | "http_steps"
    | "formulas"
    | "format_convert"
    | "math_functions"
    | "operators"
    | undefined;

export interface TagTextInputProps {
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    categories?: ParameterCategory[];
    formFields?: App.Data.FormFieldData[];
    customFields?: CustomFieldData[];
    disabled?: boolean;
    mode?: "free" | "strict-single" | "tag-only";
    maxTags?: number;
    /** Merged onto the editor's outer container, alongside its default border/rounded styling. */
    className?: string;
    onDebugTagsChange?: (
        tags: {
            inner: string;
            valid: boolean;
            paramType: TagParamType;
        }[]
    ) => void;
}

export interface InsertTagPayload {
    inner: string;
    paramType?: TagParamType;
    replaceAll?: boolean;
    displayLabel?: string;
    nodeType?: string;
}

export interface InsertRawTextPayload {
    /** Literal text to insert, e.g. "{custom_}", "toBool()", or "{step.parsedBody.}" */
    text: string;
    /** Caret position within `text` (0-indexed) to place after insertion */
    cursorOffset: number;
    replaceAll?: boolean;
}

export interface TextSegment {
    type: "text";
    value: string;
}

export interface TagSegment {
    type: "tag";
    inner: string;
    displayLabel: string;
    paramType: TagParamType;
}

export interface GhostState {
    inner: string;
    paramType: TagParamType;
    x: number;
    y: number;
}

export interface DropIndicatorState {
    x: number;
    y: number;
    height: number;
}

export type Segment = TextSegment | TagSegment;
export type ValidationLookup = Map<string, Exclude<TagParamType, undefined>>;
export type PrefixValidationLookup = Array<{ prefix: string; paramType: Exclude<TagParamType, undefined> }>;
export type TagInputMode = NonNullable<TagTextInputProps["mode"]>;

// ============================================================================
// Command
// ============================================================================

export const INSERT_TAG_COMMAND: LexicalCommand<InsertTagPayload> = createCommand("INSERT_TAG_COMMAND");
export const INSERT_RAW_TEXT_COMMAND: LexicalCommand<InsertRawTextPayload> = createCommand("INSERT_RAW_TEXT_COMMAND");
